import { z } from 'zod'
import {
  CatalogItemSchema,
  FxResponseSchema,
  ItemHistorySchema,
  OfferMatchSchema,
  StoreSchema,
  TemplateSchema,
  TripSummarySchema,
  type CatalogItem,
  type FxResponse,
  type ItemHistory,
  type OfferMatch,
  type Store,
  type Template,
  type TripSummary,
} from '@sp/shared'
import { api } from './api.ts'
import { useResource, type ResourceCache } from './useResource.ts'

/**
 * Data skupiny s kopii v zarizeni. Planovani i nakupovani musi jit otevrit
 * bez signalu, a k tomu je potreba znat obchody, sablonu a kurzy.
 */
function cacheOf<T>(key: string | null, schema: z.ZodType<T>): ResourceCache<T> {
  return {
    key,
    parse: (data) => {
      const parsed = schema.safeParse(data)
      return parsed.success ? parsed.data : null
    },
  }
}

const StoresSchema = z.array(StoreSchema)
const CatalogSchema = z.array(CatalogItemSchema)
const TripsSchema = z.array(TripSummarySchema)
const NamesSchema = z.array(z.string())
const MatchesSchema = z.array(OfferMatchSchema)
const HistorySchema = z.array(ItemHistorySchema)
const TemplatesSchema = z.array(TemplateSchema)

/** Sablony skupiny s jejich zbozim (id a mnozstvi); zbozi samo je v useCatalog. */
export function useTemplates(groupId: string | null) {
  return useResource<Template[]>(
    () => (groupId ? api.templates.list(groupId) : Promise.resolve([])),
    [groupId],
    cacheOf(groupId ? `sp:sablony:${groupId}` : null, TemplatesSchema),
  )
}

export function useStores(groupId: string | null, includeArchived = false) {
  return useResource<Store[]>(
    () => (groupId ? api.stores.list(groupId, includeArchived) : Promise.resolve([])),
    [groupId, includeArchived],
    cacheOf(groupId ? `sp:obchody:${groupId}:${includeArchived ? 'vse' : 'aktivni'}` : null, StoresSchema),
  )
}

export function useCatalog(groupId: string | null) {
  return useResource<CatalogItem[]>(
    () => (groupId ? api.catalog.list(groupId) : Promise.resolve([])),
    [groupId],
    cacheOf(groupId ? `sp:sablona:${groupId}` : null, CatalogSchema),
  )
}

export function useTrips(groupId: string) {
  return useResource<TripSummary[]>(() => api.trips.list(groupId), [groupId], cacheOf(`sp:nakupy:${groupId}`, TripsSchema))
}

export function useFrequent(groupId: string) {
  return useResource<string[]>(() => api.trips.frequent(groupId), [groupId], cacheOf(`sp:caste:${groupId}`, NamesSchema))
}

export function useFx() {
  return useResource<FxResponse>(() => api.fx(), [], cacheOf('sp:kurzy', FxResponseSchema))
}

/**
 * Polozky sablony, ktere jsou v akci — ted, nebo v den nakupu (date). Z cache
 * i bez signalu — akce maji platnost, prosle se odfiltruji.
 */
export function useOfferMatches(groupId: string | null, date?: string | null, source?: string | null) {
  return useResource<OfferMatch[]>(
    () => (groupId ? api.offers.matches(groupId, date, source) : Promise.resolve([])),
    [groupId, date ?? null, source ?? null],
    cacheOf(
      groupId ? `sp:akce-sablony:${groupId}${date ? `:${date}` : ''}${source ? `:${source}` : ''}` : null,
      MatchesSchema,
    ),
  )
}

/** Koupene polozky sablony z dokoncenych nakupu — rytmus nakupu a vyvoj cen. */
export function useHistory(groupId: string | null) {
  return useResource<ItemHistory[]>(
    () => (groupId ? api.history(groupId) : Promise.resolve([])),
    [groupId],
    cacheOf(groupId ? `sp:historie:${groupId}` : null, HistorySchema),
  )
}
